import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
interface PhotoGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  captions?: string[];
  initialIndex?: number;
}
export function PhotoGalleryModal({
  isOpen,
  onClose,
  images,
  captions = [],
  initialIndex = 0
}: PhotoGalleryModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex]);
  const nextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % images.length);
  };
  const prevImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  };
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  return <AnimatePresence data-id="element-295" data-mp="2az328zuxki78">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/95 backdrop-blur-sm p-4 md:p-8" onClick={onClose} data-id="element-296" data-mp="2aybdo8zr3kqg">
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-4 right-4 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors z-50 p-2" data-id="element-297" data-mp="2tqdnp1hw02rq">
            <X className="w-8 h-8" data-id="element-298" data-mp="35rc9bgf55rjl" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 md:top-8 md:left-8 text-white/70 font-medium z-50 bg-black/20 px-3 py-1 rounded-full backdrop-blur-md" data-id="element-299" data-mp="3aqyjti3l4top">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Navigation Buttons */}
          <button onClick={prevImage} className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors z-50 p-2 hover:bg-white/10 rounded-full" data-id="element-300" data-mp="3kg8dh2e2ukex">
            <ChevronLeft className="w-10 h-10" data-id="element-301" data-mp="3tfn4c2y5owzy" />
          </button>

          <button onClick={nextImage} className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors z-50 p-2 hover:bg-white/10 rounded-full" data-id="element-302" data-mp="2an17pzwu9rt">
            <ChevronRight className="w-10 h-10" data-id="element-303" data-mp="244cupb255q9w" />
          </button>

          {/* Main Image */}
          <div className="relative w-full h-full max-w-6xl max-h-[85vh] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-304" data-mp="1wktujs0rnge3">
            <motion.img key={currentIndex} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2
        }} src={images[currentIndex]} alt={`Gallery image ${currentIndex + 1}`} className="w-full h-full object-contain rounded-lg shadow-2xl" data-id="element-305" data-mp="1mr38ets43i1k" />

            {captions[currentIndex] && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full backdrop-blur-md text-sm font-medium flex items-center gap-2" data-id="element-306" data-mp="u5l75z14x4cv">
                <ImageIcon className="w-4 h-4" data-id="element-307" data-mp="24305bj8zus6c" />
                {captions[currentIndex]}
              </motion.div>}
          </div>

          {/* Thumbnail Strip */}
          <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2 no-scrollbar" onClick={e => e.stopPropagation()} data-id="element-308" data-mp="2wr5qu87vgl78">
            {images.map((img, idx) => <button key={idx} onClick={() => setCurrentIndex(idx)} className={`relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0 rounded-lg overflow-hidden transition-all ${currentIndex === idx ? 'ring-2 ring-amber-500 scale-105 opacity-100' : 'opacity-50 hover:opacity-80'}`} data-id="element-309" data-mp="2rr180bjhredu" data-mpi={idx}>
                <img src={img} alt="" className="w-full h-full object-cover" data-id="element-310" data-mp="3uikk1qe6cu4q" />
              </button>)}
          </div>
        </motion.div>}
    </AnimatePresence>;
}